/**
 * Token Validation Utility
 *
 * Industry Standard: Always validate JWT tokens before use
 * Prevents: Malformed token errors, security vulnerabilities
 *
 * JWT Structure: header.payload.signature
 * Each part must be base64url encoded
 */

/**
 * Validates JWT token structure
 * @param {string} token - JWT token to validate
 * @returns {boolean} true if valid JWT structure
 */
export const isValidJWT = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // Remove 'Bearer ' prefix if present
  const cleanToken = token.startsWith('Bearer ')
    ? token.substring(7)
    : token;

  // JWT must have exactly 3 parts
  const parts = cleanToken.split('.');
  if (parts.length !== 3) {
    return false;
  }

  // Validate each part is properly base64url encoded
  try {
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      // Skip signature validation (part 3)
      if (i === 2) continue;

      // Convert base64url to base64
      const base64 = part
        .replace(/-/g, '+')
        .replace(/_/g, '/');

      // Add padding if needed
      const pad = base64.length % 4;
      const padded = pad ? base64 + '==='.substring(0, 4 - pad) : base64;

      // Try to decode
      const decoded = atob(padded);

      // For header and payload, should be valid JSON
      if (i < 2) {
        JSON.parse(decoded);
      }
    }

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Extracts and validates JWT payload
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
export const decodeJWT = (token) => {
  if (!isValidJWT(token)) {
    return null;
  }

  try {
    const cleanToken = token.startsWith('Bearer ')
      ? token.substring(7)
      : token;

    const parts = cleanToken.split('.');
    const payload = parts[1];

    // Convert base64url to base64
    const base64 = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    // Add padding if needed
    const pad = base64.length % 4;
    const padded = pad ? base64 + '==='.substring(0, 4 - pad) : base64;

    // Decode and parse
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    console.error('❌ Token decode error');
    return null;
  }
};

/**
 * Checks if JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} true if expired
 */
export const isTokenExpired = (token) => {
  const payload = decodeJWT(token);

  if (!payload || !payload.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const now = Math.floor(Date.now() / 1000);
  const isExpired = payload.exp < now;

  return isExpired;
};

/**
 * Validates token; clears storage only if the token is MALFORMED.
 *
 * An EXPIRED access pass is normal — it lives one hour — and is simply reported as not usable. It is
 * left in storage, and so is the renewable token, so the next renewal (session restore, the timer or a
 * 401 retry, all via utils/sessionRenewal.js) replaces it silently. Fixed 2026-11-03
 * (tasks/owner-mobile-app-build.md §10 step 4): this used to wipe ALL auth storage on expiry, and it
 * runs at module load and on every global-axios request, so «تذكرني» never survived opening the app (or
 * waking the laptop) more than an hour after the last renewal — the daily re-logins.
 *
 * A malformed token is a genuine session end. The device binding (`pushDeviceId`) is kept even then:
 * the person who comes back through the login screen is overwhelmingly the same person on the same
 * handset, usually because they just tapped a notification. On a SHARED handset an inherited id is
 * safe: every server-side use filters on the session's own `userId` (`services/notifications/index.js`).
 *
 * @param {string} token - Token to validate
 * @returns {boolean} true if valid and not expired
 */
export const validateAndCleanToken = (token) => {
  if (!isValidJWT(token)) {
    cleanupAuthStorage({ keepDeviceBinding: true });
    return false;
  }

  return !isTokenExpired(token);
};

/**
 * Cleans up all auth-related data from storage
 * Industry standard: Clean slate on auth issues
 *
 * @param {Object}  [opts]
 * @param {boolean} [opts.keepDeviceBinding=false] keep `pushDeviceId`. Set ONLY by
 *        `validateAndCleanToken` for a malformed token — see its doc: a session end the operator walks
 *        back through immediately on the same handset. The default stays "delete", so any NEW caller
 *        must opt in deliberately.
 */
export const cleanupAuthStorage = ({ keepDeviceBinding = false } = {}) => {
  const keysToRemove = [
    'token',
    'refreshToken',
    'userId',
    'userRole',
    'userName',
    'userLocale',
    'user',
    // Account-switcher selection is session state, not UI state: leaving it behind
    // lets the next user inherit the previous one's account filter.
    'hostme_selected_account',
    // «تذكرني» choice of the current sign-in (see rememberSession below)
    'sessionRemembered'
  ];

  // This browser's push-device id belongs to the person who registered it. On a shared ops handset
  // — the exact case the registration handover path exists for — leaving it behind means the next
  // person's notification taps are attributed to their colleague's device.
  //
  // ⚠️ The exemption is a DEFECT FIX (2026-08-02), not a convenience. This key was unconditional,
  // and `validateAndCleanToken` runs on every app boot — including at module load — so a
  // one-hour-old access token deleted the id out from under the operator. A push arrives precisely
  // when the app is NOT in use, so that was the normal path, not an edge case:
  // `NotificationEvent.openedAt` was stamped on every tap and `PushSubscription.lastPushDrivenOpen`
  // never was. The screen built to find DEAD handsets therefore reported a live, actively-tapped one
  // as never used (D87) — exactly backwards.
  //
  // Since 2026-11-03 an expired pass no longer comes through here at all (it is renewed silently);
  // only a malformed token does, which is a genuine session end. Full reasoning, and the
  // shared-handset case, in `validateAndCleanToken`'s doc above.
  if (!keepDeviceBinding) keysToRemove.push('pushDeviceId');

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

// ====== «تذكرني» ======
// Unticked means "don't keep me signed in once I leave": the app may renew while it is open, but opening
// it again after the 1-hour pass has expired asks for the password (AuthContext.restoreSession). A shared
// office PC must not reopen into someone's account for 7 days. Ticked: silent renewal for 90 days.
// A sign-in from before this key existed has no value and is treated as remembered.
export const rememberSession = (remember) => {
  localStorage.setItem('sessionRemembered', remember ? '1' : '0');
};

export const isSessionRemembered = () => localStorage.getItem('sessionRemembered') !== '0';

// ====== Session ownership ======
// Clearing storage alone cannot end a session: a token refresh already in flight
// resolves afterwards and writes a fresh pair back, resurrecting it. Every write of a
// rotated token pair goes through storeTokenPair(), which refuses to persist anything
// issued for a session that has since ended.
let sessionEnded = false;

/**
 * Marks the current session as over and clears its storage.
 * Any refresh response still in flight is discarded when it arrives.
 */
export const endSession = () => {
  sessionEnded = true;
  cleanupAuthStorage();
};

/**
 * Opens a new session. Called on login, so refreshes are accepted again.
 */
export const beginSession = () => {
  sessionEnded = false;
};

// Set only when the server rejects the refresh token — i.e. the session genuinely ran
// out. A deliberate logout must never set it, so the two read differently to the user.
const SESSION_EXPIRED_KEY = 'sessionExpired';

/**
 * Records that the session expired, so the login page can say so.
 */
export const markSessionExpired = () => {
  sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
};

/**
 * Reads and clears the expiry notice. One-shot: the message shows once.
 * @returns {boolean} true if the last session ended by expiry
 */
export const consumeSessionExpired = () => {
  const expired = sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
  sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  return expired;
};

/**
 * Persists a rotated token pair. Single writer for every refresh path in the app.
 * @param {Object} tokens - { accessToken, refreshToken } from the refresh endpoint
 * @returns {boolean} false if the session ended while the request was in flight
 */
export const storeTokenPair = ({ accessToken, refreshToken } = {}) => {
  if (sessionEnded) {
    return false;
  }

  if (!accessToken || !isValidJWT(accessToken)) {
    return false;
  }

  localStorage.setItem('token', accessToken);
  sessionStorage.setItem('token', accessToken);

  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  }

  return true;
};

/**
 * Gets token from storage with validation
 * @returns {string|null} Valid token or null
 */
export const getValidToken = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  if (!token) {
    return null;
  }

  if (!validateAndCleanToken(token)) {
    return null;
  }

  return token;
};

/**
 * Stores token with validation
 * @param {string} token - Token to store
 * @param {boolean} remember - Store in localStorage (true) or sessionStorage (false)
 * @returns {boolean} true if stored successfully
 */
export const storeToken = (token, remember = true) => {
  if (!isValidJWT(token)) {
    console.error('❌ Cannot store invalid token');
    return false;
  }

  if (isTokenExpired(token)) {
    console.error('❌ Cannot store expired token');
    return false;
  }

  if (remember) {
    localStorage.setItem('token', token);
  } else {
    sessionStorage.setItem('token', token);
  }

  return true;
};

// Auto-cleanup on module load (app initialization)
(() => {
  const existingToken = localStorage.getItem('token') || sessionStorage.getItem('token');

  if (existingToken) {
    validateAndCleanToken(existingToken);
  }
})();

export default {
  isValidJWT,
  decodeJWT,
  isTokenExpired,
  validateAndCleanToken,
  cleanupAuthStorage,
  getValidToken,
  storeToken,
  storeTokenPair,
  beginSession,
  endSession,
  markSessionExpired,
  consumeSessionExpired,
  rememberSession,
  isSessionRemembered
};